/**
 * API методы для n8n RAG Chat Integration
 */

import axios, { AxiosInstance } from 'axios';
import { chatLogger } from '@/utils/chatLogger';

/**
 * Конфигурация n8n API
 */
export interface N8nApiConfig {
  /** Базовый URL n8n (например http://localhost:5678) */
  baseURL: string;
  /** Timeout для запросов (мс) */
  timeout?: number;
  /** Debug режим */
  debug?: boolean;
  /** Полный URL endpoint для чата (если задан, baseURL игнорируется для чата) */
  chatEndpoint?: string;
}

/**
 * Контекст страницы для аналитики
 */
export interface PageContext {
  /** URL текущей страницы */
  pageUrl: string;
  /** Заголовок страницы */
  pageTitle: string;
  /** Откуда пришёл пользователь */
  referrer: string;
  /** UTM метки */
  utm?: {
    source?: string;
    medium?: string;
    campaign?: string;
    term?: string;
    content?: string;
  };
}

/**
 * Сообщение в истории для n8n
 */
export interface HistoryMessage {
  role: 'user' | 'assistant';
  content: string;
  timestamp?: number;
}

/**
 * Запрос к Chat API
 */
export interface ChatRequest {
  /** Сообщение пользователя */
  message: string;
  /** Язык (ru, en, de) */
  language?: string;
  /** Base64 изображение (опционально) */
  image?: string;
  /** MIME тип изображения */
  imageMimeType?: string;
  /** ID беседы (для сохранения контекста) */
  conversationId?: string;
  /** ID пользователя */
  userId?: string;
  /** Контекст страницы */
  context?: PageContext;
  /** История сообщений (последние N для контекста) */
  history?: HistoryMessage[];
}

/**
 * Ответ от Chat API
 */
/**
 * Кнопка действия от n8n
 */
export interface ActionButton {
  /** Уникальный ID кнопки — отправляется как сообщение при клике */
  id: string;
  /** Текст, отображаемый на кнопке */
  label: string;
}

export interface ChatResponse {
  /** Успешность запроса */
  success: boolean;
  /** Ответ AI (legacy) */
  response?: string;
  /** Ответ AI (n8n формат) */
  output?: string;
  /** Количество найденных источников */
  sources_count?: number;
  /** ID беседы (legacy) */
  conversationId?: string;
  /** ID беседы (n8n формат) */
  id?: string;
  /** Показать форму захвата лида (legacy) */
  showLeadForm?: boolean;
  /** Показать форму захвата лида (n8n: true/false) */
  lead?: boolean;
  /** Причина показа формы лида (intent_detected, error, manual) */
  leadFormReason?: string;
  /** Кнопки действий для пользователя */
  buttons?: ActionButton[];
  /** Информация о распознанной машине (при загрузке фото) */
  carInfo?: {
    brand?: string;
    model?: string;
    generation?: string;
    compatible?: boolean;
    products?: string[];
  };
  /** Стадия разговора (n8n) */
  stage?: string;
}

/**
 * Получить текущий контекст страницы
 */
export function getPageContext(): PageContext {
  const url = new URL(window.location.href);
  const params = url.searchParams;
  
  return {
    pageUrl: window.location.href,
    pageTitle: document.title,
    referrer: document.referrer,
    utm: {
      source: params.get('utm_source') || undefined,
      medium: params.get('utm_medium') || undefined,
      campaign: params.get('utm_campaign') || undefined,
      term: params.get('utm_term') || undefined,
      content: params.get('utm_content') || undefined,
    },
  };
}

/**
 * n8n Chat API Service
 */
export class N8nApiService {
  private client: AxiosInstance;
  private debug: boolean;
  private apiKey?: string;
  private chatEndpoint: string;

  constructor(config: N8nApiConfig & { apiKey?: string }) {
    this.debug = config.debug || false;
    this.apiKey = config.apiKey;
    // Полный URL для чата или дефолтный путь
    this.chatEndpoint = config.chatEndpoint || `${config.baseURL}/webhook/chat`;
    
    this.client = axios.create({
      baseURL: config.baseURL,
      timeout: config.timeout || 90000,
      headers: {
        'Content-Type': 'application/json',
        ...(this.apiKey && { 'X-API-Key': this.apiKey }),
      },
    });

    // Request interceptor
    this.client.interceptors.request.use(
      (requestConfig) => {
        if (this.debug) {
          chatLogger.log('[n8n API Request]', {
            method: requestConfig.method?.toUpperCase(),
            url: requestConfig.url,
            data: requestConfig.data,
          });
        }
        return requestConfig;
      },
      (error) => {
        if (this.debug) {
          chatLogger.error('[n8n API Request Error]', error);
        }
        return Promise.reject(error);
      }
    );

    // Response interceptor
    this.client.interceptors.response.use(
      (response) => {
        if (this.debug) {
          chatLogger.log('[n8n API Response]', {
            status: response.status,
            data: response.data,
          });
        }
        return response;
      },
      (error) => {
        if (this.debug) {
          chatLogger.error('[n8n API Response Error]', {
            status: error.response?.status,
            message: error.message,
            data: error.response?.data,
          });
        }
        return Promise.reject(error);
      }
    );
  }

  /**
   * Отправить сообщение в RAG Chat (текст и/или изображение)
   * Все запросы идут на один endpoint, n8n сам решает как обработать
   */
  async sendMessage(request: ChatRequest, signal?: AbortSignal): Promise<ChatResponse> {
    const enrichedRequest = {
      ...request,
      context: request.context || getPageContext(),
    };

    if (this.debug) {
      chatLogger.log('[n8n API] Отправка на:', this.chatEndpoint, {
        hasImage: !!request.image,
        messageLength: request.message?.length,
      });
    }

    const response = await this.client.post<ChatResponse>(this.chatEndpoint, enrichedRequest, {
      timeout: 90000,
      signal,
    });
    return response.data;
  }

  /**
   * Отправить сообщение с изображением (для распознавания машины)
   * Использует тот же endpoint что и текстовые сообщения
   */
  async sendMessageWithImage(
    request: Omit<ChatRequest, 'image'> & { imageFile: File },
    signal?: AbortSignal
  ): Promise<ChatResponse> {
    const base64 = await this.fileToBase64(request.imageFile);
    
    return this.sendMessage({
      message: request.message || 'Identify this car and check LARTE Design kit compatibility',
      language: request.language,
      image: base64,
      imageMimeType: request.imageFile.type,
      conversationId: request.conversationId,
      userId: request.userId,
      history: request.history,
      context: request.context,
    }, signal);
  }

  /**
   * Конвертация файла в base64
   */
  private fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const result = reader.result as string;
        // Убираем префикс data:image/...;base64,
        const base64 = result.split(',')[1];
        resolve(base64);
      };
      reader.onerror = (error) => reject(error);
    });
  }

  /**
   * Отправка лида (обратный звонок/контакт)
   */
  async sendLeadCapture(
    payload: {
      name?: string;
      phone: string;
      email?: string;
      language?: string;
      userId?: string;
      conversationId?: string;
      context?: PageContext;
      /** Preferred time slot: morning, afternoon, evening */
      preferredContactTime?: string;
      /** Client timezone, e.g. Europe/Berlin */
      timezone?: string;
      /** ISO country code from phone, e.g. DE */
      countryCode?: string;
      /** Preferred contact channel: telegram, whatsapp, email, phone */
      preferredChannel?: string;
      /** Telegram username if channel=telegram */
      telegramUsername?: string;
    },
    endpoint = '/webhook/lead-capture'
  ): Promise<{ success: boolean }> {
    const body = {
      ...payload,
      context: payload.context || getPageContext(),
    };
    const response = await this.client.post(endpoint, body);
    return response.data;
  }
}

